import React, { useState, useEffect, useMemo } from 'react';
import { DarijaBuddyAdmin } from '../DarijaBuddy';
import SEOManagementPanel from './admin/SEOManagementPanel';
import LoginSecurityPanel from './admin/LoginSecurityPanel';
import ApiSecurityPanel from './admin/ApiSecurityPanel';
import LearningClockAdmin from './admin/LearningClockAdmin';
import AdminTravelTools from './admin/AdminTravelTools';
import AdminBlog from './admin/blog/AdminBlog';
import ConversationChallengeEditor from './admin/ConversationChallengeEditor';
import SceneExplorerAdmin from './admin/SceneExplorerAdmin';
import SoukClothesAdmin from './admin/SoukClothesAdmin';
import SoukGameAdmin from './admin/SoukGameAdmin';
import WeatherTab from './admin/WeatherTab';
import WordPuzzleAdmin from './admin/WordPuzzleAdmin';
import MembershipAdmin from './admin/MembershipAdmin';
import ManageProfileAdmin from './admin/ManageProfileAdmin';
import OrdersAdmin from './admin/OrdersAdmin';
import FinancialAdmin from './admin/FinancialAdmin';
import PaymentSettingsAdmin from './admin/PaymentSettingsAdmin';
import VerbExplorerAdmin from './admin/VerbExplorerAdmin';
import FaqsAdmin from './admin/FaqsAdmin';
import DocumentationAdmin from './admin/DocumentationAdmin';
import StaticPagesAdmin from './admin/StaticPagesAdmin';
import ExploreAdmin from './admin/ExploreAdmin';
import MoroccoJourneyAdmin from './admin/MoroccoJourneyAdmin';
import DecoderAdmin from './admin/DecoderAdmin';
import GamesAdmin from './admin/GamesAdmin';
import MaintenanceAdmin from './admin/MaintenanceAdmin';
import DashboardAdmin from './admin/DashboardAdmin';
import SystemAdmin from './admin/SystemAdmin';
import WebsiteDesignsAdmin from './admin/WebsiteDesignsAdmin';
import CommunicationAdmin from './admin/CommunicationAdmin';
import SystemManagerAdmin from './admin/SystemManagerAdmin';
import WordBankAdmin from './admin/WordBankAdmin';
import AudioManagerAdmin from './admin/AudioManagerAdmin';
import DictionaryAdmin from './admin/DictionaryAdmin';
import TranslationsAdmin from './admin/TranslationsAdmin';
import GrammarAdminTab from './admin/GrammarAdminTab';
import BrainAdminTab from './admin/BrainAdminTab';
import MusicAdminPanel from './admin/MusicAdminPanel';
import LearningAdmin from './admin/LearningAdmin';
import DailyWordsAdmin from './admin/DailyWordsAdmin';
import LanguagesTabWithUI from './admin/LanguagesTabWithUI';
import HelpContentTab from './admin/HelpContentTab';
import AmazighAdminTab from './admin/AmazighAdminTab';
import SoukGameAdminSection from './admin/SoukGameAdminSection';
import CulturePodAdminPanel from './admin/CulturePodAdminPanel';
import ImageManager from './admin/ImageManager';
import SoundLibrary from './admin/SoundLibrary';
import LandingPageAdmin from './admin/LandingPageAdmin';
import MobilePageAdmin from './admin/MobilePageAdmin';
import LetterFallAdmin from './admin/LetterFallAdmin';
import LetterEaterAdmin from './admin/LetterEaterAdmin';

const AdminPanel = ({ API, adminToken, languages, onLanguagesUpdate, onSettingsUpdate, showNotification, isDarkMode }) => {
  // Party particle dots for admin preview
  const adminPartyDots = React.useMemo(() => {
    const colors = ['#ff4444','#ff7733','#ffdd33','#44ff44','#33ddff','#4477ff','#9944ff','#ff44cc',
                    '#ff6666','#ffaa44','#ffff44','#66ff66','#44ffdd','#6699ff','#bb66ff','#ff66dd',
                    '#ff3355','#ff8800','#aaff33','#33ffaa','#3399ff','#aa44ff','#ff33aa','#ffcc00'];
    return Array.from({length: 24}, (_, i) => {
      const angle = (i / 24) * Math.PI * 2;
      const startR = 10 + (i % 4) * 5;
      return {
        style: {
          left: `calc(50% + ${Math.round(Math.cos(angle) * startR)}px)`,
          top: `calc(50% + ${Math.round(Math.sin(angle) * startR)}px)`,
          width: `${3 + (i % 3) * 2}px`,
          height: `${3 + (i % 3) * 2}px`,
          backgroundColor: colors[i],
        }
      };
    });
  }, []);
  const adminConfettiPieces = React.useMemo(() => {
    const colors = ['#ffc600', '#159b36'];
    return Array.from({length: 30}, (_, i) => ({
      style: {
        left: `${(i / 30) * 100}%`,
        top: `${15 + (i % 5) * 15}%`,
        width: `${6 + (i % 3)}px`,
        height: `${3 + (i % 2)}px`,
        backgroundColor: colors[i % 2],
      }
    }));
  }, []);
  const adminVoltageDots = React.useMemo(() => {
    const colors = ['#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d',
                    '#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff'];
    return Array.from({length: 16}, (_, i) => ({
      style: {
        left: `${8 + (i / 16) * 84}%`,
        top: i < 8 ? '5%' : '95%',
        width: '8px',
        height: '8px',
        backgroundColor: colors[i],
        color: colors[i],
      }
    }));
  }, []);
  const [isMounting, setIsMounting] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  // Translations state moved to TranslationsAdmin.js

  const [isLoading, setIsLoading] = useState(false);
  const [cardEdits, setCardEdits] = useState({});  // { translationId: { gender, number, audioUrl } }
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    sourceText: '',
    translatedText: '',
    latinScript: '',
    gender: '',
    number: '',
    audioUrl: '',
    audioEnabled: true,
    note: '',
    // Variants support for edit form
    variants: [],        // Existing variants from database
    newVariants: [],     // New variants to add
    isLoadingVariants: false
  });
  
  // Helper functions for edit form variants
  const addEditVariant = () => {
    setEditFormData(prev => ({
      ...prev,
      newVariants: [...prev.newVariants, { latin: '', arabic: '', isNew: true }]
    }));
  };
  
  const removeEditVariant = (index, isNew) => {
    if (isNew) {
      setEditFormData(prev => ({
        ...prev,
        newVariants: prev.newVariants.filter((_, i) => i !== index)
      }));
    }
    // Note: Existing variants deletion would need a separate API call
  };
  
  const updateEditVariant = (index, field, value, isNew) => {
    if (isNew) {
      setEditFormData(prev => ({
        ...prev,
        newVariants: prev.newVariants.map((v, i) => i === index ? { ...v, [field]: value } : v)
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value, modified: true } : v)
      }));
    }
  };
  
  // Fetch variants for a translation when editing
  const fetchVariantsForEdit = async (sourceText) => {
    try {
      const res = await fetch(`${API}/translate?text=${encodeURIComponent(sourceText)}&sourceLang=en`, {
        method: 'GET'
      });
      if (res.ok) {
        const data = await res.json();
        return data.variants || [];
      }
    } catch (err) {
      console.error('Failed to fetch variants:', err);
    }
    return [];
  };
  
  const [adminLanguages, setAdminLanguages] = useState([]);  // All languages including inactive
  


  // showMaintenanceEnableModal/DisableModal — moved to MaintenanceAdmin.js
  // showForceCacheClearModal, showClearCacheModal — moved to SystemAdmin.js
  
  const [settings, setSettings] = useState({
    audioGloballyEnabled: true,
    voiceInputEnabled: true,
    autoTranslationEnabled: false,
    genderIconsEnabled: true,
    numberIconsEnabled: true,
    maintenanceMode: false,
    // Multi-Language Translation Pipeline Settings
    multiLanguageEnabled: true,
    lingvaApiUrl: 'https://lingva.ml',
    // Groq AI Settings (DISABLED by default)
    groqAiEnabled: false,
    groqAiModel: 'llama-3.3-70b-versatile',
    genderIconConfig: {
      male: { icon: "♂ M", bgColor: "blue", textColor: "blue" },
      female: { icon: "♀ F", bgColor: "pink", textColor: "pink" },
      both: { icon: "👥 Both", bgColor: "purple", textColor: "purple" }
    },
    numberIconConfig: {
      singular: { icon: "S", bgColor: "green", textColor: "green" },
      plural: { icon: "P", bgColor: "orange", textColor: "orange" }
    },
    maintenanceConfig: {
      message: "We're currently performing scheduled maintenance",
      details: "We'll be back shortly. Thank you for your patience!",
      icon: "🔧",
      backgroundType: "gradient",
      backgroundValue: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      scheduledStart: null,
      scheduledEnd: null,
      customCSS: "",
      showCountdown: true,
      enableRetry: true
    }
  });

  const [badges, setBadges] = useState([]);

  const loadBadgesData = async () => {
    try {
      const badgesRes = await fetch(`${API}/admin/badges`, { headers: authHeaders });
      if (badgesRes.ok) {
        const badgesData = await badgesRes.json();
        setBadges(Array.isArray(badgesData) ? badgesData : []);
      } else {
        setBadges([]);
      }
    } catch (error) {
      console.error("Error loading badges:", error);
    }
  };

  // Word Bank Admin States moved to WordBankAdmin.js

  // Conversation Challenge States — dead code removed (ConversationChallengeEditor is self-contained)


  // editingBadge, editBadgeData, newBadge moved to WordBankAdmin.js

  // Membership states
  // -- Membership admin states moved to MembershipAdmin.js --

  // Dictionary states — moved to DictionaryAdmin.js
  // Protected Features states moved to SystemManagerAdmin.js
  
  // FAQ states — moved to FaqsAdmin.js
  
  // Avatar states
  
  // Announcement states moved to CommunicationAdmin.js

  // Documentation states — moved to DocumentationAdmin.js
  const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  
  // Backup & Restore states moved to SystemAdmin.js

  
  // Morocco Journey states — moved to MoroccoJourneyAdmin.js
  
  // Static Pages states — moved to StaticPagesAdmin.js
  
  // Message states
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [newMessageToUser, setNewMessageToUser] = useState(null);
  
  const [editCategoryData, setEditCategoryData] = useState({
    name: '',
    description: '',
    icon: '',
    order: 0
  });
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    icon: '📚',
    order: 0
  });
  const [selectedCategoryForItems, setSelectedCategoryForItems] = useState(null);
  const [categoryItemsList, setCategoryItemsList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  // Decoder state — moved to DecoderAdmin.js
  // Dictionary admin state (kept — used by dictionary tab)
  // Dictionary admin state — moved to DictionaryAdmin.js
  // Explorer admin state — moved to ExploreAdmin.js
  
  // (mood state removed - replaced by decoder/explorer state above)

  // Verb Explorer — moved to VerbExplorerAdmin.js

  // Forms state
  const [newTranslation, setNewTranslation] = useState({
    sourceText: "",
    sourceLang: "en",
    translatedText: "",
    latinScript: "",
    gender: "",
    number: "",
    status: "verified",
    audioUrl: "",
    audioEnabled: true,
    // Variant support for manual "Add New Translation"
    variantRows: []
  });

  // Duplicate detection state
  const [duplicateCheck, setDuplicateCheck] = useState({ show: false, existing: null, pendingEvent: null });

  // Helper functions for newTranslation variants
  const addNewTranslationVariant = () => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: [...prev.variantRows, { latin: "", arabic: "", gender: "", number: "" }]
    }));
  };
  
  const removeNewTranslationVariant = (index) => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: prev.variantRows.filter((_, i) => i !== index)
    }));
  };
  
  const updateNewTranslationVariant = (index, field, value) => {
    setNewTranslation(prev => ({
      ...prev,
      variantRows: prev.variantRows.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      )
    }));
  };

  // 🔒 PROTECTED: Translation Stats - Track manual entries (AI must NEVER delete these)
  const [translationStats, setTranslationStats] = useState({
    total: 0,
    singleWords: 0,
    sentences: 0,
    isLoading: false
  });
  
  // Fetch translation stats
  const fetchTranslationStats = async () => {
    if (!adminToken) return;
    setTranslationStats(prev => ({ ...prev, isLoading: true }));
    try {
      const res = await fetch(`${API}/admin/translations/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTranslationStats({
          total: data.total || 0,
          singleWords: data.singleWords || 0,
          sentences: data.sentences || 0,
          isLoading: false
        });
      }
    } catch (err) {
      console.error('Failed to fetch translation stats:', err);
      setTranslationStats(prev => ({ ...prev, isLoading: false }));
    }
  };


  // AI Translation state moved to TranslationsAdmin.js


  // systemManagerSubTab moved to SystemManagerAdmin.js
  // communicationSubTab moved to CommunicationAdmin.js
  // Website design states moved to WebsiteDesignsAdmin.js

  // Load data based on active tab

  // Prevent login form flash on refresh
  useEffect(() => {
    // Small delay to check if adminToken exists in props
    const timer = setTimeout(() => {
      setIsMounting(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  // Feedback/special words loading moved to TranslationsAdmin.js

  // Protected features data loading moved to SystemManagerAdmin.js

  // Announcements data loading moved to CommunicationAdmin.js

  // Load settings on component mount to ensure we have current maintenance mode state
  useEffect(() => {
    const loadInitialSettings = async () => {
      try {
        const settingsRes = await fetch(`${API}/admin/settings`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings(settingsData);
        }
      } catch (error) {
        console.error('Error loading initial settings:', error);
      }
    };
    loadInitialSettings();
  }, []); // Run once on mount

  // Load languages when component mounts or updates
  useEffect(() => {
    if (languages.length === 0) {
      onLanguagesUpdate();
    }
  }, [languages, onLanguagesUpdate]);

  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  // For FormData requests, don't set Content-Type (browser sets it automatically with boundary)
  const authHeadersForForm = {
    'Authorization': `Bearer ${adminToken}`
  };

  // Backup functions moved to SystemAdmin.js

  // FEATURE FLAGS FUNCTIONS
  

  // Payment settings loading moved to PaymentSettingsAdmin.js

  // Decoder functions — moved to DecoderAdmin.js

  const loadTabData = async () => {
    setIsLoading(true);
    try {
      switch (activeTab) {
        // case "dashboard" — data loading handled by DashboardAdmin component
        // case "orders" and "financial" — data loading handled by OrdersAdmin and FinancialAdmin components
        case "translations":
          // Data loading handled by TranslationsAdmin component
          break;
        // case "dictionary" — data loading moved to DictionaryAdmin.js
        case "languages":
          // Fetch all languages including inactive ones for admin
          const languagesRes = await fetch(`${API}/admin/languages`, {
            headers: authHeaders
          });
          if (languagesRes.ok) {
            const languagesData = await languagesRes.json();
            setAdminLanguages(Array.isArray(languagesData) ? languagesData : []);
          } else {
            setAdminLanguages([]);
          }
          break;
        case "settings":
          // Fetch app settings
          const settingsRes = await fetch(`${API}/admin/settings`, {
            headers: authHeaders
          });
          if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            setSettings(settingsData);
          }
          break;
        // faqs — data loading moved to FaqsAdmin.js
        // darija-decoder — data loading moved to DecoderAdmin.js
        // verb-explorer — data loading moved to VerbExplorerAdmin.js
        // explore — data loading moved to ExploreAdmin.js
        case "membership":
          // Data loading handled by MembershipAdmin component
          break;
        // games — data loading moved to GamesAdmin.js
        // documentation — data loading moved to DocumentationAdmin.js
        // static-pages — data loading moved to StaticPagesAdmin.js
        // morocco-journey — data loading moved to MoroccoJourneyAdmin.js
        // word-bank — data loading moved to WordBankAdmin.js
        default:
          break;
      }
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification("Failed to load data", "error");
    } finally {
      setIsLoading(false);
    }
  };
  

  // -- Membership user management functions moved to MembershipAdmin.js --


  // ============================================
  // GROQ AI DICTIONARY FUNCTIONS — moved to DictionaryAdmin.js
  // Announcement management functions moved to CommunicationAdmin.js

  // Games Management Functions — moved to GamesAdmin.js
  // Conversation Challenge Settings Handler
  const handleUpdateConversationSettings = async (field, value) => {
    try {
      // Update local state first for immediate UI feedback
      const currentConvSettings = settings.conversationChallenge || {};
      const updatedConvSettings = {
        ...currentConvSettings,
        [field]: value
      };
      
      setSettings({
        ...settings,
        conversationChallenge: updatedConvSettings
      });

      // Then update backend
      const response = await fetch(`${API}/admin/settings/conversation-challenge`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          conversationChallenge: updatedConvSettings
        })
      });

      if (response.ok) {
        showNotification('Conversation Challenge settings updated', 'success');
        // Refresh global app settings to update frontend Games display
        if (onSettingsUpdate) {
          onSettingsUpdate();
        }
      } else {
        // Revert on error
        setSettings({
          ...settings,
          conversationChallenge: currentConvSettings
        });
        showNotification('Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Error updating conversation settings:', error);
      showNotification('Failed to update settings', 'error');
    }
  };

  // Conversation Challenge functions — dead code removed (ConversationChallengeEditor is self-contained)


  // FAQ MANAGEMENT FUNCTIONS — moved to FaqsAdmin.js
  // FAQ functions -- moved to FaqsAdmin.js
  // Documentation functions -- moved to DocumentationAdmin.js
  // Static Pages functions -- moved to StaticPagesAdmin.js
  // -- Membership functions moved to MembershipAdmin.js --


  const handleSeedData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API}/seed-data`, { method: "POST" });

      if (response.ok) {
        showNotification("Sample data created successfully!", "success");
        // Reload languages and translations
        await onLanguagesUpdate();
        await loadTabData();
      } else {
        throw new Error("Failed to seed data");
      }
    } catch (error) {
      console.error("Error seeding data:", error);
      showNotification("Failed to seed data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // handleForceCacheClear moved to SystemAdmin.js

  // Admin notes handlers moved to DashboardAdmin.js

  // Translation CRUD functions moved to TranslationsAdmin.js

  const handleToggleLanguage = async (languageId) => {
    try {
      const response = await fetch(`${API}/admin/languages/${languageId}/toggle`, {
        method: "PUT",
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, "success");
        // Reload languages
        loadTabData();
        // Also update frontend languages
        await onLanguagesUpdate();
      } else {
        throw new Error("Failed to toggle language");
      }
    } catch (error) {
      console.error("Toggle error:", error);
      showNotification("Failed to toggle language", "error");
    }
  };

  // handleToggleSetting — shared by TranslationsAdmin and WordBankAdmin
  const handleToggleSetting = async (settingName, value) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: value
    }));
    try {
      const url = `${API}/admin/settings?${settingName}=${value}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: authHeaders
      });
      if (response.ok) {
        showNotification("Settings updated successfully!", "success");
        if (onSettingsUpdate) {
          await onSettingsUpdate();
        }
      } else {
        setSettings(prev => ({
          ...prev,
          [settingName]: !value
        }));
        throw new Error("Failed to update settings");
      }
    } catch (error) {
      console.error("Settings error:", error);
      showNotification(`Failed to update settings: ${error.message}`, "error");
    }
  };

  // handleUpdateMaintenanceConfig — moved to MaintenanceAdmin.js


  // SpeechGen & Audio Manager functions moved to AudioManagerAdmin.js

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "system-manager", label: "System Manager", icon: "⚙️" },
    { id: "maintenance", label: "Maintenance Mode", icon: "🚧" },
    { id: "travel-tools", label: "Travel Tools", icon: "🇲🇦" },
    { id: "blog", label: "Blog", icon: "✈️" },
    { id: "music", label: "Music", icon: "🎵" },
    { id: "culture-pod", label: "Culture Pod", icon: "🎧" },
    { id: "word-puzzle", label: "Word Puzzle", icon: "🧩" },
    { id: "orders", label: "Order History", icon: "🛒" },
    { id: "payment-settings", label: "Payment Settings", icon: "💳" },
    { id: "financial", label: "Financial", icon: "💰" },
    { id: "souk-game", label: "D-Bargain Souk game", icon: "🛍️" },
    { id: "souk-clothes", label: "Souk L7wayej Game", icon: "👗" },
    { id: "morocco-journey", label: "Morocco Journey Game", icon: "🏜️" },
    { id: "translations", label: "Translations", icon: "🔄" },
    { id: "grammar", label: "Grammar", icon: "📝" },
    { id: "brain", label: "Brain", icon: "🧠" },
    { id: "verb-explorer", label: "Verb Explorer", icon: "📖" },
    { id: "dictionary", label: "Dictionary", icon: "📖" },
    { id: "darija-decoder", label: "Darija Decoder", icon: "🔮" },
    { id: "amazigh", label: "Amazigh", icon: "ⵣ" },
    { id: "explore", label: "Explore", icon: "🎧" },
    { id: "darija-buddy", label: "Darija Buddy", icon: "🤖" },
    { id: "documentation", label: "Documentation", icon: "📄" },
    { id: "static-pages", label: "Static Pages", icon: "📃" },
    { id: "languages", label: "Languages", icon: "🌍" },
    { id: "communication", label: "Communication", icon: "📢" },
    { id: "games", label: "Games Management", icon: "🎮" },
    { id: "conversation-challenge", label: "Conversation Challenge", icon: "🗣️" },
    { id: "seo", label: "SEO", icon: "🔍" },
    { id: "security", label: "Login Security", icon: "🔒" },
    { id: "api-security", label: "API Security", icon: "🛡️" },
    { id: "learning-clock-admin", label: "Learning Clock", icon: "🕐" },
    { id: "scene-explorer-admin", label: "Scene Explorer", icon: "🖼️" },
    { id: "learning", label: "Learn Darija", icon: "📚" },
    { id: "daily-words", label: "Daily Words", icon: "📅" },
    { id: "website-designs", label: "Website Designs", icon: "🎨" },
    { id: "word-bank", label: "Word Bank", icon: "📦" },
    { id: "settings", label: "Settings", icon: "⚙️" },
    { id: "membership", label: "Membership", icon: "👥" },
    { id: "system", label: "System Backup", icon: "🔧" },
    { id: "manage-profile", label: "Manage Profile", icon: "👤" },
    { id: "speechgen", label: "SpeechGen", icon: "🎙️" },
    { id: "audio-manager", label: "Audio Manager", icon: "🔊" },
    { id: "sound-library", label: "Sound Library", icon: "🎵" },
    { id: "image-manager", label: "Image Manager", icon: "🖼️" },
    { id: "landing-page", label: "Landing Page", icon: "🏠" },
    { id: "mobile-page", label: "Mobile Page", icon: "📱" },
    { id: "letter-fall", label: "Letter Fall", icon: "🔤" },
    { id: "letter-eater", label: "Letter Eater", icon: "👾" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`rounded-lg shadow-sm border transition-colors duration-200 p-6 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Admin Panel
            </h1>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Manage Darija translations and monitor user activity
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400/70' : 'text-yellow-700/70'}`}>
              No API or AI saves to database — only Admin can add/edit translations, verbs, and words.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`rounded-lg shadow-sm border transition-colors duration-200 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex flex-wrap gap-2 p-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Loading...
              </span>
            </div>
          ) : (
              <div>
              {/* DASHBOARD TAB */}
              {activeTab === "dashboard" && (
                <DashboardAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}
              {activeTab === "system-manager" && (
                <SystemManagerAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* MY MOROCCO TAB */}
              {activeTab === "travel-tools" && (
                <AdminTravelTools adminToken={adminToken} />
              )}

              {/* BLOG TAB — own module, isolated from other admin features */}
              {activeTab === "blog" && (
                <AdminBlog
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                />
              )}

              {/* MUSIC TAB */}
              {activeTab === "music" && (
                <MusicAdminPanel
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                />
              )}
              {activeTab === "culture-pod" && (
                <CulturePodAdminPanel
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                />
              )}
              {activeTab === "word-puzzle" && (
                <WordPuzzleAdmin
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                />
              )}

              {/* TRANSLATIONS TAB */}
              {activeTab === "translations" && (
                <TranslationsAdmin
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                  settings={settings}
                  setSettings={setSettings}
                  languages={languages}
                  onLanguagesUpdate={onLanguagesUpdate}
                  onSettingsUpdate={onSettingsUpdate}
                  badges={badges}
                  loadBadgesData={loadBadgesData}
                />
              )}


              {/* GRAMMAR TAB */}
              {activeTab === "grammar" && (
                <GrammarAdminTab API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* BRAIN TAB */}
              {activeTab === "brain" && (
                <BrainAdminTab API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* VERB EXPLORER TAB */}
              {activeTab === "verb-explorer" && (
                <VerbExplorerAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* DICTIONARY TAB */}
              {activeTab === "dictionary" && (
                <DictionaryAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* FAQs TAB */}
              {activeTab === "faqs" && (
                <FaqsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* DOCUMENTATION TAB */}
              {activeTab === "documentation" && (
                <DocumentationAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* STATIC PAGES TAB */}
              {activeTab === "static-pages" && (
                <StaticPagesAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* DARIJA DECODER ADMIN TAB */}
              {activeTab === "darija-decoder" && (
                <DecoderAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* AMAZIGH ADMIN TAB */}
              {activeTab === "amazigh" && (
                <AmazighAdminTab API={API} isDarkMode={isDarkMode} showNotification={showNotification} adminToken={adminToken} />
              )}

              {/* EXPLORE ADMIN TAB */}
              {activeTab === "explore" && (
                <ExploreAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} onSettingsUpdate={onSettingsUpdate} />
              )}


              {/* DARIJA BUDDY ADMIN TAB */}
              {activeTab === "darija-buddy" && (
                <DarijaBuddyAdmin
                  API={API}
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                />
              )}

              {/* LANGUAGES TAB */}
              {activeTab === "languages" && (
                <LanguagesTabWithUI
                  isDarkMode={isDarkMode}
                  API={API}
                  adminToken={adminToken}
                  showNotification={showNotification}
                />
              )}


              {activeTab === "communication" && (
                <CommunicationAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} onSettingsUpdate={onSettingsUpdate} />
              )}


              {activeTab === "orders" && (
                <OrdersAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* D-BARGAIN SOUK GAME TAB - FOOD (Recipes, Items, Merchants) */}
              {activeTab === "souk-game" && (
                <SoukGameAdmin API={API} adminToken={adminToken} showNotification={showNotification} isDarkMode={isDarkMode} />
              )}

              {/* SOUK L7WAYEJ GAME TAB - CLOTHES + Gamification */}
              {activeTab === "souk-clothes" && (
                <SoukClothesAdmin 
                  API={API} 
                  adminToken={adminToken} 
                  showNotification={showNotification} 
                  isDarkMode={isDarkMode}
                />
              )}

              {/* MOROCCO JOURNEY GAME EDITOR TAB */}
              {activeTab === "morocco-journey" && (
                <MoroccoJourneyAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* PAYMENT SETTINGS TAB */}
              {activeTab === "payment-settings" && (
                <PaymentSettingsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* FINANCIAL DASHBOARD TAB */}
              {activeTab === "financial" && (
                <FinancialAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* MEMBERSHIP TAB */}
              {activeTab === "membership" && (
                <MembershipAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* SEO MANAGEMENT TAB */}
              {activeTab === "seo" && (
                <SEOManagementPanel isDarkMode={isDarkMode} adminToken={adminToken} />
              )}

              {/* LOGIN SECURITY TAB */}
              {activeTab === "security" && (
                <LoginSecurityPanel adminToken={adminToken} />
              )}

              {/* API SECURITY TAB */}
              {activeTab === "api-security" && (
                <ApiSecurityPanel isDarkMode={isDarkMode} adminToken={adminToken} />
              )}


              {/* LEARNING CLOCK ADMIN TAB */}
              {activeTab === "learning-clock-admin" && (
                <LearningClockAdmin
                  adminToken={adminToken}
                  isDarkMode={isDarkMode}
                  onBack={() => setActiveTab("bot-settings")}
                />
              )}

              {/* SCENE EXPLORER ADMIN TAB */}
              {activeTab === "scene-explorer-admin" && (
                <SceneExplorerAdmin API={API} adminToken={adminToken} showNotification={showNotification} isDarkMode={isDarkMode} />
              )}

              {/* LEARN DARIJA TAB */}
              {activeTab === "learning" && (
                <LearningAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* DAILY WORDS TAB */}
              {activeTab === "daily-words" && (
                <DailyWordsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === "website-designs" && (
                <WebsiteDesignsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {/* SETTINGS TAB */}
              {activeTab === "settings" && (
                <div className="space-y-6">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    App Settings
                  </h3>


                </div>
              )}


              {/* Membership modals moved to MembershipAdmin.js */}

              {/* Avatar & Gender modals moved to ManageProfileAdmin.js */}


              {/* Force Cache Clear modal moved to SystemAdmin.js */}

              {/* Delete Message Confirmation Modal */}
              {showDeleteMessageModal && messageToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="mb-6">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                          <span className="text-4xl">🗑️</span>
                        </div>
                      </div>
                      <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Delete This Message?
                      </h3>
                      <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        This action cannot be undone. The message will be permanently deleted.
                      </p>
                      
                      {/* Message Preview */}
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                        <p className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {messageToDelete.subject}
                        </p>
                        <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          From: {messageToDelete.from_user_name} ({messageToDelete.from_user_email})
                        </p>
                        <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {messageToDelete.content}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteMessageModal(false);
                          setMessageToDelete(null);
                        }}
                        className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                          isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${API}/admin/messages/${messageToDelete.id}`, {
                              method: 'DELETE',
                              headers: {
                                'Authorization': `Bearer ${adminToken}`
                              }
                            });
                            
                            if (response.ok) {
                              showNotification('Message deleted successfully', 'success');
                              setShowDeleteMessageModal(false);
                              setMessageToDelete(null);
                              loadTabData();
                            } else {
                              const errorData = await response.json();
                              showNotification(errorData.detail || 'Failed to delete message', 'error');
                            }
                          } catch (error) {
                            console.error('Error deleting message:', error);
                            showNotification('Failed to delete message', 'error');
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold"
                      >
                        Delete Message
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Restore/Delete Backup modals moved to SystemAdmin.js */}

              {/* GAMES MANAGEMENT TAB */}
              {activeTab === "games" && (
                <GamesAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}
              {/* CONVERSATION CHALLENGE EDITOR TAB */}
              {activeTab === "conversation-challenge" && (
                <div className="h-[calc(100vh-200px)]">
                  <ConversationChallengeEditor isDarkMode={isDarkMode} />
                </div>
              )}

              {/* MAINTENANCE MODE TAB */}
              {activeTab === "maintenance" && (
                <MaintenanceAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} settings={settings} setSettings={setSettings} onSettingsUpdate={onSettingsUpdate} />
              )}
              {activeTab === "system" && (
                <SystemAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}


              {activeTab === "manage-profile" && (
                <ManageProfileAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === 'speechgen' && (
                <AudioManagerAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} tab="speechgen" />
              )}

              {activeTab === 'audio-manager' && (
                <AudioManagerAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} tab="audio-manager" />
              )}

              {/* ═══════════════ SOUND LIBRARY TAB ═══════════════ */}
              {/* NEW MP3-hosting module — distinct from Audio Manager (which is AI TTS). */}
              {activeTab === 'sound-library' && (
                <SoundLibrary API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* ═══════════════ IMAGE MANAGER TAB ═══════════════ */}
              {activeTab === 'image-manager' && (
                <ImageManager API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {/* ═══════════════ LANDING PAGE TAB ═══════════════ */}
              {activeTab === 'landing-page' && (
                <LandingPageAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === 'mobile-page' && (
                <MobilePageAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === 'letter-fall' && (
                <LetterFallAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === 'letter-eater' && (
                <LetterEaterAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
              )}

              {activeTab === 'word-bank' && (
                <WordBankAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} settings={settings} setSettings={setSettings} appSettings={settings} setAppSettings={setSettings} handleToggleSetting={handleToggleSetting} badges={badges} loadBadgesData={loadBadgesData} onSettingsUpdate={onSettingsUpdate} />
              )}

            </div>
          )}
        </div>
      </div>

      {/* AI Training section removed — NLLB cleanup */}
    </div>
  );
};



export default AdminPanel;
